/**
 * Message Translation Hook
 * Provides translation functionality for chat messages based on user's language setting.
 *
 * The backend now returns `display_text` with pre-translated content when available.
 * This hook provides utilities for:
 * 1. Getting display text from messages (preferring backend translation)
 * 2. Falling back to API translation for new messages without cached translation
 * 3. Determining the original_lang when sending messages
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translationClient, type TargetLanguage } from '../lib/translation';

interface TranslationState {
    translatedText: string | null;
    isTranslating: boolean;
    error: boolean;
}

interface UseMessageTranslationOptions {
    enabled?: boolean;
}

interface MessageWithTranslation {
    message: string;
    original_lang?: string;
    display_text?: string | null;
}

/**
 * Hook for translating messages based on user's language setting
 */
export function useMessageTranslation(options: UseMessageTranslationOptions = {}) {
    const { enabled = true } = options;
    const { i18n } = useTranslation();
    const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
    const [translations, setTranslations] = useState<Map<string, TranslationState>>(new Map());
    const pendingTranslations = useRef<Set<string>>(new Set());

    // Check if translation service is configured on mount
    useEffect(() => {
        if (enabled) {
            console.log('[Translation] Checking configuration...');
            translationClient.isConfigured().then((configured) => {
                console.log('[Translation] isConfigured result:', configured);
                setIsConfigured(configured);
            }).catch((err) => {
                console.error('[Translation] isConfigured error:', err);
                setIsConfigured(false);
            });
        }
    }, [enabled]);

    /**
     * Get the user's current language code for API calls
     */
    const getUserLang = useCallback((): 'en' | 'ja' => {
        return i18n.language === 'en' ? 'en' : 'ja';
    }, [i18n.language]);

    /**
     * Get the original language when sending a message
     * Based on the user's current language setting
     */
    const getOriginalLang = useCallback((): 'en' | 'ja' => {
        return i18n.language === 'en' ? 'en' : 'ja';
    }, [i18n.language]);

    /**
     * Check if translation is needed based on user's language
     * Translation is needed for both directions:
     * - English mode: Japanese messages need translation to English
     * - Japanese mode: English messages need translation to Japanese
     */
    const needsTranslation = useCallback(() => {
        // Translation is available if enabled and configured
        return enabled && isConfigured === true;
    }, [enabled, isConfigured]);

    // Get target language based on user's current language
    const getTargetLanguage = useCallback((): TargetLanguage => {
        return i18n.language === 'en' ? 'EN' : 'JA';
    }, [i18n.language]);

    // Get source language (opposite of target)
    const getSourceLanguage = useCallback((): TargetLanguage => {
        return i18n.language === 'en' ? 'JA' : 'EN';
    }, [i18n.language]);

    /**
     * Get display text for a message.
     * Priority:
     * 1. If message's original_lang matches user's lang, return original message
     * 2. If display_text is available from backend, use it
     * 3. Fall back to original message
     */
    const getDisplayText = useCallback((msg: MessageWithTranslation): string => {
        const userLang = getUserLang();

        // If original language matches user's language, no translation needed
        if (msg.original_lang === userLang) {
            return msg.message;
        }

        // If backend provided a translation, use it
        if (msg.display_text && msg.display_text !== msg.message) {
            return msg.display_text;
        }

        // Fall back to original message
        return msg.message;
    }, [getUserLang]);

    /**
     * Translate a message for display (fallback when backend doesn't have translation)
     * Used when receiving messages in the opposite language without cached translation
     */
    const translateForDisplay = useCallback(async (
        messageId: string | number,
        text: string,
        originalLang?: string
    ): Promise<string | null> => {
        const userLang = getUserLang();

        // If original language matches user's language, no translation needed
        if (originalLang === userLang) {
            return null;
        }

        if (!needsTranslation() || !text.trim()) {
            return null;
        }

        const key = `display-${messageId}`;

        // Check if already translated
        const existing = translations.get(key);
        if (existing?.translatedText) {
            return existing.translatedText;
        }

        // Check if translation is in progress
        if (pendingTranslations.current.has(key)) {
            return null;
        }

        // Start translation
        pendingTranslations.current.add(key);
        setTranslations(prev => new Map(prev).set(key, {
            translatedText: null,
            isTranslating: true,
            error: false,
        }));

        try {
            console.log('[Translation] Calling API for:', text.substring(0, 30), 'target:', getTargetLanguage());
            const result = await translationClient.translate(text, getTargetLanguage(), getSourceLanguage());
            console.log('[Translation] API success:', result.translated_text.substring(0, 30));

            setTranslations(prev => new Map(prev).set(key, {
                translatedText: result.translated_text,
                isTranslating: false,
                error: false,
            }));

            return result.translated_text;
        } catch (error) {
            console.error('[Translation] API error:', error);
            setTranslations(prev => new Map(prev).set(key, {
                translatedText: null,
                isTranslating: false,
                error: true,
            }));
            return null;
        } finally {
            pendingTranslations.current.delete(key);
        }
    }, [needsTranslation, getTargetLanguage, getSourceLanguage, translations, getUserLang]);

    /**
     * Get translation state for a message
     */
    const getTranslationState = useCallback((messageId: string | number): TranslationState | null => {
        return translations.get(`display-${messageId}`) || null;
    }, [translations]);

    /**
     * Check if user is in English mode (needs translation for display)
     */
    const isEnglishMode = i18n.language === 'en';

    /**
     * Clear all translations (useful when changing language)
     */
    const clearTranslations = useCallback(() => {
        setTranslations(new Map());
        pendingTranslations.current.clear();
    }, []);

    // Clear translations when language changes
    useEffect(() => {
        clearTranslations();
    }, [i18n.language, clearTranslations]);

    return {
        // State
        isConfigured,
        isEnglishMode,
        needsTranslation: needsTranslation(),
        userLang: getUserLang(),

        // Methods
        getDisplayText,
        getOriginalLang,
        translateForDisplay,
        getTranslationState,
        clearTranslations,
    };
}

export default useMessageTranslation;
